/**
 * fetchWithRetry.ts
 *
 * A fetch() wrapper with a per-attempt timeout (AbortController) and a few retries with a short
 * backoff. Real-world mobile connections -- even over WiFi, even on the SAME local network as
 * the server -- can have brief stalls that cause an individual request to fail or hang, even
 * though the underlying connection is fine moments later: iOS/WebKit in particular suspends a
 * backgrounded tab's JS execution and in-flight network activity fairly aggressively (e.g. the
 * screen auto-locking, or switching apps briefly), which can silently kill or indefinitely
 * delay a `fetch()` that was already in progress.
 *
 * This was confirmed happening in practice (not theoretical): a user's initial load appeared
 * permanently stuck on "Loading GFS manifest...", and nginx's access log showed the request
 * sequence eventually DID complete successfully, just with an unexplained multi-minute gap in
 * the middle where the page had no way to know whether to keep waiting or give up. Without
 * retries, a single such stall among the many fetches fired during startup can sink the whole
 * page to a degraded fallback even though the vast majority of real data was actually available
 * moments later.
 *
 * Usable from both the main thread and the particle physics Web Worker -- fetch/AbortController/
 * setTimeout are all available in both contexts (none of these are DOM-specific APIs).
 */
export async function fetchWithRetry(url: string, attempts = 3, timeoutMs = 20000): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
      if (attempt < attempts - 1) {
        // Short, increasing backoff before the next attempt -- not scientifically tuned, just
        // enough to let a transient blip (the device waking back up, a brief WiFi hiccup) clear.
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

/**
 * Fetches a URL's full body as an ArrayBuffer, resuming via HTTP Range requests across attempts
 * instead of restarting from byte zero every time (what plain fetchWithRetry effectively does).
 *
 * This matters a lot for the large (multi-MB) binary files this app loads (GFS pressure levels
 * are ~10MB each, the elevation grid is ~50MB): confirmed in production, a sufficiently slow or
 * congested remote connection can simply be unable to finish downloading one of these within any
 * single reasonable per-attempt timeout, no matter how many times it's retried from scratch --
 * each attempt makes the same partial progress and then times out again, forever. As long as the
 * server honors Range requests (nginx's static file serving does by default, confirmed via its
 * `Accept-Ranges: bytes` response header), resuming from wherever the previous attempt left off
 * means every attempt is forward progress: a file will eventually finish even on a very slow
 * link, it just takes more (cheap, partial) attempts rather than needing one big attempt to
 * complete the whole transfer in one window.
 *
 * Falls back to re-fetching from scratch if the server doesn't honor the Range header (responds
 * with 200 instead of 206) -- shouldn't happen against this app's own backend, but keeps this
 * safe to reuse against anything else in the future.
 */
export async function fetchArrayBufferResumable(url: string, attempts = 8, timeoutMs = 20000): Promise<ArrayBuffer> {
  const chunks: Uint8Array[] = [];
  let receivedBytes = 0;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const headers: HeadersInit | undefined = receivedBytes > 0 ? { Range: `bytes=${receivedBytes}-` } : undefined;
      const response = await fetch(url, { signal: controller.signal, headers });
      if (!response.ok && response.status !== 206) throw new Error(`HTTP ${response.status}`);
      if (receivedBytes > 0 && response.status !== 206) {
        // Server ignored the Range request and sent the whole thing again from byte 0.
        chunks.length = 0;
        receivedBytes = 0;
      }
      const reader = response.body?.getReader();
      if (!reader) {
        // No streaming body reader available (shouldn't happen in any browser this app targets)
        // -- fall back to buffering the whole response in one shot.
        const buffer = await response.arrayBuffer();
        clearTimeout(timeoutId);
        return buffer;
      }
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedBytes += value.byteLength;
      }
      clearTimeout(timeoutId);
      const result = new Uint8Array(receivedBytes);
      let offset = 0;
      for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.byteLength;
      }
      return result.buffer;
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
      if (attempt < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}
